import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import {
  DownloadStatusesType,
  ResultMessageType,
} from "./ShutdownDownloadXlsxPageTypes";

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function isBlobResponse(value: unknown): value is Blob {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Blob).text === "function"
  );
}

export function getDownloadPanelClassName(): string {
  return "flex w-full max-w-xl flex-col items-center justify-center gap-4 rounded-xl border-2 border-slate-700 bg-slate-100 px-6 py-8 text-center shadow-sm sm:px-8";
}

export function getDateInputClassName(): string {
  return "w-full rounded-lg border border-slate-700 bg-white px-3 py-2 text-base text-gray-900";
}

function ShutdownDownloadXlsxPage() {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<string>(todayIsoDate());
  const [dateTo, setDateTo] = useState<string>(todayIsoDate());
  const [msg, setMsg] = useState<ResultMessageType>({
    text: "",
    status: DownloadStatusesType.None,
  });

  const formEnabled = Boolean(dateFrom && dateTo);
  const isLoading = msg.status === DownloadStatusesType.Loading;

  const download = () => {
    setMsg({ text: "Скачивается", status: DownloadStatusesType.Loading });

    monitorApi
      .downloadShutdownXlsx(dateFrom, dateTo)
      .then((response) => {
        const filename = `shutdowns_${dateFrom}_${dateTo}.xlsx`;
        triggerBlobDownload(response.data, filename);
        setMsg({
          text: "Файл скачан",
          status: DownloadStatusesType.Ok,
        });
      })
      .catch(async (error) => {
        let errorText = error.message;
        const responseData = error.response?.data;
        if (isBlobResponse(responseData)) {
          try {
            const parsed = JSON.parse(await responseData.text());
            if (parsed?.data) {
              errorText = parsed.data;
            }
          } catch {
            /* keep default error message */
          }
        } else if (responseData?.data) {
          errorText = responseData.data;
        }
        setMsg({
          text: errorText,
          status: DownloadStatusesType.Error,
        });
      });
  };

  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />

      <div className="py-10 w-full max-w-4xl mx-auto px-4 sm:px-5 md:px-8">
        <div className="mb-2 flex justify-center">
          <div
            className={getDownloadPanelClassName()}
            data-testid="shutdown-download-panel"
          >
            <span className="text-xl font-semibold text-gray-900 sm:text-2xl">
              Скачать excel табличку
            </span>
            <span className="text-base text-gray-700 sm:text-lg">
              выберите период
            </span>
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-2 text-left">
                <span className="text-sm font-medium text-gray-800">От</span>
                <input
                  className={getDateInputClassName()}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="shutdown-download-date-from"
                />
              </label>
              <label className="flex flex-col gap-2 text-left">
                <span className="text-sm font-medium text-gray-800">До</span>
                <input
                  className={getDateInputClassName()}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="shutdown-download-date-to"
                />
              </label>
            </div>
            <p
              className="text-sm text-gray-600"
              data-testid="shutdown-download-export-note"
            >
              {t(
                "shutdown_download_xlsx.export_note",
                "В файл не попадают записи без региона или без типа шатдауна.",
              )}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-xl">
          <StatusLabel text={msg.text} status={msg.status} />

          <button
            disabled={!formEnabled || isLoading}
            className={
              formEnabled
                ? "mt-2 w-full rounded-lg border border-gray-950 bg-slate-100 px-4 py-3 text-lg font-medium transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-slate-50 disabled:text-gray-500 disabled:opacity-35"
                : "invisible mt-2 w-full"
            }
            onClick={download}
            data-testid="shutdown-download-button"
          >
            {t("shutdown_download_xlsx.download_button_text", "Скачать")}
          </button>
        </div>
      </div>
    </main>
  );
}

function StatusLabel({
  text,
  status,
}: {
  text: string;
  status: DownloadStatusesType;
}) {
  if (status === DownloadStatusesType.None || !text) {
    return <div className="invisible p-2 mb-1">{text}</div>;
  }

  if (
    status === DownloadStatusesType.Ok ||
    status === DownloadStatusesType.Loading
  ) {
    return <div className="bg-green-200 p-2 mb-1">{text}</div>;
  }

  return <div className="bg-red-200 p-2 mb-1">{text}</div>;
}

export { isBlobResponse };
export default ShutdownDownloadXlsxPage;
